import type { GmRewardType } from './utils/types';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Button, buttonVariants } from '@/components/ui/Button/button';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import { GmGoldBadge } from '../GmGoldBadge';

interface Props {
  reward: GmRewardType;
  onClick?: VoidFunction;
  disabled?: boolean;
  className?: string;
}

const replaceKeyImg = 'IMG_KEY';

// TODO: need to confirm imgs will be static or not
const baseSrc = `/assets/image/gamification/rewards/${replaceKeyImg}@2x.webp`;

export const GmAwardCard = ({
  onClick,
  disabled,
  reward,
  className,
}: Props) => {
  const {
    title,
    href,
    isClaim,
    statusName,
    subTitle,
    availableAmount,
    imgKey,
  } = reward;
  const t = useTranslations('gamification');

  // Boolean() keeps an amount of 0 from being rendered as a stray "0".
  const showBadge = disabled || Boolean(availableAmount);

  const getBadgeText = () => {
    if (availableAmount) {
      return availableAmount;
    }

    if (statusName) {
      return t('need-status', {
        status: statusName.toLocaleString().toUpperCase(),
      });
    }

    return t('coming-soon');
  };

  const actionText = isClaim ? t('claim-now') : t('open');
  const actionClassName = buttonVariants({ variant: 'primary', size: 'md', className: 'h-8 px-4' });

  return (
    <div className={cn('rounded-xl relative glass-stroke-border shadow-card gm-award-card-gradient px-4 py-3', className)}>
      {
        // TODO: need confirmation about text in badge
        showBadge && (
          <GmGoldBadge
            variant="desktop"
            className="absolute top-1 min-w-7 right-1 z-1 py-[0.438rem] border-0 gm-gold-gradient"
          >
            {getBadgeText()}
          </GmGoldBadge>
        )
      }
      <div className="flex max-w-40 h-full justify-between flex-col items-start">
        <div className={cn('flex flex-col gap-1', disabled && 'opacity-40')}>
          <p className="font-normal text-gradient bg-promo-banner-title-gradient w-fit text-xl leading-5">
            {title}
          </p>
          {
            subTitle && (
              <p className="text-base font-medium leading-6">
                {subTitle}
              </p>
            )
          }
        </div>
        {
          // disabled must block navigation too, so fall back to a disabled Button instead of a Link
          href && !disabled
            ? (
                <Link href={href} className={actionClassName}>
                  {actionText}
                </Link>
              )
            : (
                <Button
                  type="button"
                  variant="primary"
                  onClick={onClick}
                  disabled={disabled}
                  className={cn(actionClassName, 'relative')}
                >
                  {actionText}
                </Button>
              )
        }
      </div>
      <div className={cn('right-0 top-0 h-35 w-42.5 absolute', disabled && 'opacity-40')}>
        <Image
          fill
          alt={title}
          src={baseSrc.replace(replaceKeyImg, imgKey)}
        />
      </div>
    </div>
  );
};
